"use client";

import { useTranslations } from "next-intl";
import React, { useEffect, useRef, useState } from "react";

type LanguageSkill = {
  languageName: string;
  skillLevel: number;
};

type LanguageSkillProps = {
  languages: Array<LanguageSkill>;
};

const CEFR_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

export function LanguageSkill({ languages }: LanguageSkillProps) {
  const t = useTranslations(
    "HomePage.resume.professionalProfile.languagesCard.cardInfo"
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex-1 flex flex-col gap-8 p-10 rounded-lg bg-secondary-grey min-w-xs"
    >
      <div className="flex flex-col flex-nowrap">
        <h2>{t("title")}</h2>
        <p>{t("description")}</p>
      </div>
      {languages.map((lang, index) => {
        const progressPercent = (lang.skillLevel / CEFR_LEVELS.length) * 100;
        return (
          <div key={index} className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-sm">{lang.languageName}</span>
            </div>

            <div className="w-full flex flex-col gap-1">
              <div className="flex justify-around text-xs text-primary-yellow px-1">
                {CEFR_LEVELS.map((level) => (
                  <span key={level}>{level}</span>
                ))}
              </div>

              <div className="w-full h-3 bg-background-grey rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-yellow transition-all duration-1000 ease-in-out"
                  style={{ width: inView ? `${progressPercent}%` : "0%" }}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
